class TimelineSection {
  constructor() {
    this.sliderComponent = document.querySelector('.timeline__slider');
    if (!this.sliderComponent) return;

    this.slider = this.sliderComponent.querySelector('[id^="Slider-"]');
    this.sliderItems = this.sliderComponent.querySelectorAll('[id^="Slide-"]');
    this.dotsContainer = this.sliderComponent.querySelector('[id^="TimelineDots-"]');
    // Exclude blank dots from the dots array (only get dots with data-slide-index)
    this.dots = this.dotsContainer ? Array.from(this.dotsContainer.querySelectorAll('.timeline__dot[data-slide-index]')) : [];
    this.isMobile = window.innerWidth < 990;
    
    // Get dots display settings from the timeline element
    const timelineElement = this.sliderComponent.closest('.timeline');
    this.dotsDesktop = timelineElement ? parseInt(timelineElement.dataset.dotsDesktop || '10', 10) : 10;
    this.dotsMobile = timelineElement ? parseInt(timelineElement.dataset.dotsMobile || '4', 10) : 4;
    
    if (!this.slider || !this.dotsContainer || this.dots.length === 0) return;

    this.currentSlideIndex = 0;
    this.init();
  }

  init() {
    // Listen for slider changes
    this.sliderComponent.addEventListener('slideChanged', (e) => {
      const slideIndex = e.detail.currentPage - 1;
      this.updateActiveDot(slideIndex);
    });

    // Prevent scrolling past the last slide
    this.slider.addEventListener('scroll', () => {
      this.preventOverscroll();
    });

    // Add click handlers to dots
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goToSlide(index);
      });
    });

    // Handle mobile arrow buttons manually (slider component only attaches to first buttons found)
    this.setupMobileButtons();

    // Setup desktop navigation buttons (attach listeners to all instances)
    this.setupDesktopButtons();

    // Handle window resize for mobile/desktop switching
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth < 990;
        if (wasMobile !== this.isMobile) {
          this.updateDotsVisibility();
        }
      }, 250);
    });

    // Initial update
    this.updateActiveDot(0);
    this.updateDotsVisibility();
  }

  setupMobileButtons() {
    // Find mobile arrow buttons
    const mobileArrows = this.sliderComponent.querySelector('.timeline__mobile-arrows');
    if (!mobileArrows) return;

    const mobilePrev = mobileArrows.querySelector('button[name="previous"]');
    const mobileNext = mobileArrows.querySelector('button[name="next"]');

    if (!mobilePrev || !mobileNext) return;

    // Wait for slider component to initialize
    setTimeout(() => {
      // Create event object that matches what slider component expects
      const createButtonEvent = (buttonName) => {
        return {
          preventDefault: () => {},
          stopPropagation: () => {},
          currentTarget: {
            name: buttonName,
            dataset: {}
          }
        };
      };

      mobilePrev.addEventListener('click', (e) => {
        if (window.innerWidth < 990) {
          e.preventDefault();
          e.stopPropagation();
          
          // Directly call the slider component's onButtonClick method
          if (this.sliderComponent.onButtonClick) {
            const fakeEvent = createButtonEvent('previous');
            this.sliderComponent.onButtonClick(fakeEvent);
          } else {
            // Fallback: manually scroll
            const currentScroll = this.slider.scrollLeft;
            if (this.sliderComponent.sliderItemOffset) {
              const newPosition = Math.max(0, currentScroll - this.sliderComponent.sliderItemOffset);
              if (this.sliderComponent.setSlidePosition) {
                this.sliderComponent.setSlidePosition(newPosition);
              } else {
                this.slider.scrollTo({ left: newPosition, behavior: 'smooth' });
              }
            }
          }
        }
      });

      mobileNext.addEventListener('click', (e) => {
        if (window.innerWidth < 990) {
          e.preventDefault();
          e.stopPropagation();
          
          // Directly call the slider component's onButtonClick method
          if (this.sliderComponent.onButtonClick) {
            const fakeEvent = createButtonEvent('next');
            this.sliderComponent.onButtonClick(fakeEvent);
          } else {
            // Fallback: manually scroll
            const currentScroll = this.slider.scrollLeft;
            const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
            if (this.sliderComponent.sliderItemOffset) {
              const newPosition = Math.min(maxScroll, currentScroll + this.sliderComponent.sliderItemOffset);
              if (this.sliderComponent.setSlidePosition) {
                this.sliderComponent.setSlidePosition(newPosition);
              } else {
                this.slider.scrollTo({ left: newPosition, behavior: 'smooth' });
              }
            }
          }
        }
      });

      // Also sync disabled states from desktop buttons to mobile buttons
      this.syncButtonStates();
      this.slider.addEventListener('scroll', () => {
        this.syncButtonStates();
      });
    }, 150);
  }

  syncButtonStates() {
    const desktopPrev = this.sliderComponent.querySelector('.timeline__desktop-navigation button[name="previous"]');
    const desktopNext = this.sliderComponent.querySelector('.timeline__desktop-navigation button[name="next"]');
    const mobilePrev = this.sliderComponent.querySelector('.timeline__mobile-arrows button[name="previous"]');
    const mobileNext = this.sliderComponent.querySelector('.timeline__mobile-arrows button[name="next"]');

    if (desktopPrev && mobilePrev) {
      if (desktopPrev.hasAttribute('disabled')) {
        mobilePrev.setAttribute('disabled', 'disabled');
      } else {
        mobilePrev.removeAttribute('disabled');
      }
    }

    if (desktopNext && mobileNext) {
      if (desktopNext.hasAttribute('disabled')) {
        mobileNext.setAttribute('disabled', 'disabled');
      } else {
        mobileNext.removeAttribute('disabled');
      }
    }
  }

  preventOverscroll() {
    if (!this.slider || this.sliderItems.length === 0) return;
    
    // Only prevent overscroll if we're past the maximum scroll position
    // Don't interfere with scroll snapping by forcing scroll position
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    const currentScroll = this.slider.scrollLeft;
    
    // Allow a small tolerance for scroll snapping to work properly
    if (currentScroll > maxScroll + 10) {
      this.slider.scrollLeft = maxScroll;
    }
  }

  updateActiveDot(activeIndex) {
    this.currentSlideIndex = activeIndex;
    
    // Scroll dots container to center/position the active dot
    this.scrollDotsToActive(activeIndex);
    
    // Update dot active states
    this.dots.forEach((dot, index) => {
      if (index === activeIndex) {
        dot.classList.add('timeline__dot--active');
        dot.classList.remove('timeline__dot--previous');
        dot.classList.remove('timeline__dot--previous2');
        dot.classList.remove('timeline__dot--previous3');
        dot.classList.remove('timeline__dot--next');
        dot.classList.remove('timeline__dot--next2');
        dot.classList.remove('timeline__dot--next3');
      } else {
        dot.classList.remove('timeline__dot--active');
        
        // On mobile, mark dots to show (always 4 dots)
        if (this.isMobile) {
          const totalDots = this.dots.length;
          const isLastSlide = activeIndex === totalDots - 1;
          const isSecondLastSlide = activeIndex === totalDots - 2;
          const isThirdLastSlide = activeIndex === totalDots - 3;
          
          // Determine which dots to show:
          // - If at first slide: show active, next, next+1, next+2
          // - If at last slide (and 4+ dots): show active-3, active-2, active-1, active
          // - If at second-to-last slide: show active-2, active-1, active, active+1
          // - If at third-to-last slide: show active-1, active, active+1, active+2
          // - Otherwise: show previous, active, next, next+1
          let isVisible = false;
          
          // If fewer than 4 dots, show all dots
          if (totalDots < 4) {
            isVisible = true;
          } else if (activeIndex === 0) {
            // First slide: show next 3 dots
            isVisible = index === activeIndex + 1 || index === activeIndex + 2 || index === activeIndex + 3;
          } else if (isLastSlide) {
            // Last slide: show previous 3 dots
            isVisible = index === activeIndex - 3 || index === activeIndex - 2 || index === activeIndex - 1;
          } else if (isSecondLastSlide) {
            // Second-to-last slide: show previous 2, active, next
            isVisible = index === activeIndex - 2 || index === activeIndex - 1 || index === activeIndex + 1;
          } else if (isThirdLastSlide) {
            // Third-to-last slide: show previous 1, active, next, next+1
            isVisible = index === activeIndex - 1 || index === activeIndex + 1 || index === activeIndex + 2;
          } else {
            // Other slides: show previous, next, next+1
            isVisible = index === activeIndex - 1 || index === activeIndex + 1 || index === activeIndex + 2;
          }
          
          // Remove all visibility classes first
          dot.classList.remove('timeline__dot--previous');
          dot.classList.remove('timeline__dot--previous2');
          dot.classList.remove('timeline__dot--previous3');
          dot.classList.remove('timeline__dot--next');
          dot.classList.remove('timeline__dot--next2');
          dot.classList.remove('timeline__dot--next3');
          
          // Add appropriate class based on position
          if (isVisible) {
            if (activeIndex === 0) {
              if (index === activeIndex + 1) {
                dot.classList.add('timeline__dot--next');
              } else if (index === activeIndex + 2) {
                dot.classList.add('timeline__dot--next2');
              } else if (index === activeIndex + 3) {
                dot.classList.add('timeline__dot--next3');
              }
            } else if (isLastSlide) {
              if (index === activeIndex - 3) {
                dot.classList.add('timeline__dot--previous3');
              } else if (index === activeIndex - 2) {
                dot.classList.add('timeline__dot--previous2');
              } else if (index === activeIndex - 1) {
                dot.classList.add('timeline__dot--previous');
              }
            } else if (isSecondLastSlide) {
              if (index === activeIndex - 2) {
                dot.classList.add('timeline__dot--previous2');
              } else if (index === activeIndex - 1) {
                dot.classList.add('timeline__dot--previous');
              } else if (index === activeIndex + 1) {
                dot.classList.add('timeline__dot--next');
              }
            } else if (isThirdLastSlide) {
              if (index === activeIndex - 1) {
                dot.classList.add('timeline__dot--previous');
              } else if (index === activeIndex + 1) {
                dot.classList.add('timeline__dot--next');
              } else if (index === activeIndex + 2) {
                dot.classList.add('timeline__dot--next2');
              }
            } else {
              if (index === activeIndex - 1) {
                dot.classList.add('timeline__dot--previous');
              } else if (index === activeIndex + 1) {
                dot.classList.add('timeline__dot--next');
              } else if (index === activeIndex + 2) {
                dot.classList.add('timeline__dot--next2');
              }
            }
          }
        } else {
          dot.classList.remove('timeline__dot--previous');
          dot.classList.remove('timeline__dot--previous2');
          dot.classList.remove('timeline__dot--previous3');
          dot.classList.remove('timeline__dot--next');
          dot.classList.remove('timeline__dot--next2');
          dot.classList.remove('timeline__dot--next3');
        }
      }
    });

    this.updateDotsVisibility();
    
    // Scroll after visibility updates (with a small delay to ensure DOM has updated)
    requestAnimationFrame(() => {
      this.scrollDotsToActive(activeIndex);
    });
  }

  scrollDotsToActive(activeIndex) {
    if (!this.dotsContainer || activeIndex < 0 || activeIndex >= this.dots.length) return;
    
    const activeDot = this.dots[activeIndex];
    if (!activeDot || activeDot.style.display === 'none') return;
    
    const dotsContainer = this.dotsContainer;
    const containerWidth = dotsContainer.clientWidth;
    
    // Get the active dot's position relative to the container
    const dotLeft = activeDot.offsetLeft;
    const dotWidth = activeDot.offsetWidth;
    const dotCenter = dotLeft + (dotWidth / 2);
    
    // Calculate the desired scroll position to center the dot
    const targetScrollLeft = dotCenter - (containerWidth / 2);
    
    // Clamp the scroll position to valid bounds
    const maxScroll = dotsContainer.scrollWidth - containerWidth;
    const clampedScroll = Math.max(0, Math.min(targetScrollLeft, maxScroll));
    
    // Smooth scroll to the target position
    dotsContainer.scrollTo({
      left: clampedScroll,
      behavior: 'smooth'
    });
  }

  updateDotsVisibility() {
    // Get blank dots (they're always in the DOM)
    const blankStart = this.dotsContainer.querySelector('.timeline__dot--blank-start');
    const blankEnd = this.dotsContainer.querySelector('.timeline__dot--blank-end');
    
    const totalDots = this.dots.length;
    const isLastSlide = this.currentSlideIndex === totalDots - 1;
    
    if (!this.isMobile) {
      // Desktop: show maximum dots based on setting (or all dots if fewer than setting)
      const maxDotsDesktop = this.dotsDesktop;
      if (totalDots <= maxDotsDesktop) {
        // Show all dots if maxDotsDesktop or fewer
      this.dots.forEach(dot => {
        dot.style.display = '';
      });
        // Hide blank dots on desktop
        if (blankStart) blankStart.style.display = 'none';
        if (blankEnd) blankEnd.style.display = 'none';
      } else {
        // Show maxDotsDesktop dots around the active one
        // Calculate which dots to show
        let startIndex, endIndex;
        const dotsBefore = Math.floor((maxDotsDesktop - 1) / 2);
        const dotsAfter = Math.ceil((maxDotsDesktop - 1) / 2);
        
        // If we're near the beginning, show first maxDotsDesktop dots
        if (this.currentSlideIndex < dotsBefore) {
          startIndex = 0;
          endIndex = Math.min(maxDotsDesktop - 1, totalDots - 1);
        }
        // If we're near the end, show last maxDotsDesktop dots
        else if (this.currentSlideIndex > totalDots - dotsAfter - 1) {
          startIndex = Math.max(0, totalDots - maxDotsDesktop);
          endIndex = totalDots - 1;
        }
        // Otherwise, center around the active dot
        else {
          startIndex = this.currentSlideIndex - dotsBefore;
          endIndex = this.currentSlideIndex + dotsAfter;
        }
        
        this.dots.forEach((dot, index) => {
          if (index >= startIndex && index <= endIndex) {
            dot.style.display = '';
          } else {
            dot.style.display = 'none';
          }
        });
        
        // Hide blank dots on desktop
        if (blankStart) blankStart.style.display = 'none';
        if (blankEnd) blankEnd.style.display = 'none';
      }
      return;
    }

    // Mobile: always show exactly dotsMobile dots (or all dots if fewer than dotsMobile)
    const maxDotsMobile = this.dotsMobile;
    const isSecondLastSlide = this.currentSlideIndex === totalDots - 2;
    const isThirdLastSlide = this.currentSlideIndex === totalDots - 3;
    
    // If fewer than maxDotsMobile dots, show all dots
    if (totalDots < maxDotsMobile) {
      this.dots.forEach(dot => {
        dot.style.display = 'flex';
      });
      // Hide blank dots when showing all dots
      if (blankStart) blankStart.style.display = 'none';
      if (blankEnd) blankEnd.style.display = 'none';
      return;
    }
    
    // Show/hide blank dots based on current slide
    if (this.currentSlideIndex === 0) {
      // First slide: show blank at start, hide blank at end
      if (blankStart) blankStart.style.display = 'flex';
      if (blankEnd) blankEnd.style.display = 'none';
    } else if (isLastSlide) {
      // Last slide: hide blank at start, show blank at end
      if (blankStart) blankStart.style.display = 'none';
      if (blankEnd) blankEnd.style.display = 'flex';
    } else {
      // Other slides: hide both blanks
      if (blankStart) blankStart.style.display = 'none';
      if (blankEnd) blankEnd.style.display = 'none';
    }
    
    // Calculate how many dots to show on first and last slides
    // First slide: show blank + (maxDotsMobile - 1) actual dots
    // Last slide: show (maxDotsMobile - 1) actual dots + blank
    const dotsToShowFirst = maxDotsMobile - 1; // Excluding the blank
    const dotsToShowLast = maxDotsMobile - 1; // Excluding the blank
    
    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentSlideIndex;
      
      let isVisible = false;
      if (this.currentSlideIndex === 0) {
        // First slide: show active + next (dotsToShowFirst - 1) dots
        // The first position will be empty placeholder, so we show dotsToShowFirst dots starting from index 0
        isVisible = isActive || (index > this.currentSlideIndex && index <= this.currentSlideIndex + dotsToShowFirst - 1);
      } else if (isLastSlide) {
        // Last slide: show previous (dotsToShowLast - 1) + active
        // The last position will be empty placeholder, so we show dotsToShowLast dots ending at active
        isVisible = isActive || (index >= this.currentSlideIndex - dotsToShowLast + 1 && index < this.currentSlideIndex);
      } else {
        // Other slides: show dots centered around active, ensuring we always show maxDotsMobile dots
        const dotsBefore = Math.floor((maxDotsMobile - 1) / 2);
        const dotsAfter = Math.ceil((maxDotsMobile - 1) / 2);
        
        // Calculate the range we want to show
        let startIndex = this.currentSlideIndex - dotsBefore;
        let endIndex = this.currentSlideIndex + dotsAfter;
        
        // Adjust if we're too close to the end
        if (endIndex >= totalDots) {
          const overflow = endIndex - totalDots + 1;
          endIndex = totalDots - 1;
          startIndex = Math.max(0, startIndex - overflow);
        }
        
        // Adjust if we're too close to the beginning
        if (startIndex < 0) {
          const underflow = -startIndex;
          startIndex = 0;
          endIndex = Math.min(totalDots - 1, endIndex + underflow);
        }
        
        // Ensure we show exactly maxDotsMobile dots (or as many as available)
        const dotsToShow = Math.min(maxDotsMobile, totalDots - startIndex);
        endIndex = startIndex + dotsToShow - 1;
        
        isVisible = index >= startIndex && index <= endIndex;
      }
      
      if (isVisible) {
        dot.style.display = 'flex';
      } else {
        dot.style.display = 'none';
      }
    });
  }

  setupDesktopButtons() {
    // Find all desktop navigation buttons (one in each slide)
    const allDesktopNavs = this.sliderComponent.querySelectorAll('.timeline__desktop-navigation');
    if (!allDesktopNavs || allDesktopNavs.length === 0) return;

    // Wait for slider component to initialize
    setTimeout(() => {
      allDesktopNavs.forEach((desktopNav) => {
        const prevButton = desktopNav.querySelector('button[name="previous"]');
        const nextButton = desktopNav.querySelector('button[name="next"]');

        if (prevButton && !prevButton.hasAttribute('data-timeline-listener')) {
          prevButton.setAttribute('data-timeline-listener', 'true');
          prevButton.addEventListener('click', (e) => {
            if (window.innerWidth >= 990) {
              e.preventDefault();
              e.stopPropagation();
              
              // Directly call the slider component's onButtonClick method
              if (this.sliderComponent.onButtonClick) {
                const fakeEvent = {
                  preventDefault: () => {},
                  stopPropagation: () => {},
                  currentTarget: {
                    name: 'previous',
                    dataset: {}
                  }
                };
                this.sliderComponent.onButtonClick(fakeEvent);
              } else {
                // Fallback: manually scroll
                const currentScroll = this.slider.scrollLeft;
                if (this.sliderComponent.sliderItemOffset) {
                  const newPosition = Math.max(0, currentScroll - this.sliderComponent.sliderItemOffset);
                  if (this.sliderComponent.setSlidePosition) {
                    this.sliderComponent.setSlidePosition(newPosition);
                  } else {
                    this.slider.scrollTo({ left: newPosition, behavior: 'smooth' });
                  }
                }
              }
            }
          });
        }

        if (nextButton && !nextButton.hasAttribute('data-timeline-listener')) {
          nextButton.setAttribute('data-timeline-listener', 'true');
          nextButton.addEventListener('click', (e) => {
            if (window.innerWidth >= 990) {
              e.preventDefault();
              e.stopPropagation();
              
              // Directly call the slider component's onButtonClick method
              if (this.sliderComponent.onButtonClick) {
                const fakeEvent = {
                  preventDefault: () => {},
                  stopPropagation: () => {},
                  currentTarget: {
                    name: 'next',
                    dataset: {}
                  }
                };
                this.sliderComponent.onButtonClick(fakeEvent);
              } else {
                // Fallback: manually scroll
                const currentScroll = this.slider.scrollLeft;
                const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
                if (this.sliderComponent.sliderItemOffset) {
                  const newPosition = Math.min(maxScroll, currentScroll + this.sliderComponent.sliderItemOffset);
                  if (this.sliderComponent.setSlidePosition) {
                    this.sliderComponent.setSlidePosition(newPosition);
                  } else {
                    this.slider.scrollTo({ left: newPosition, behavior: 'smooth' });
                  }
                }
              }
            }
          });
        }
      });

      // Sync button states from the first navigation (slider component controls)
      this.syncDesktopButtonStates();
      this.slider.addEventListener('scroll', () => {
        this.syncDesktopButtonStates();
      });
    }, 150);
  }

  syncDesktopButtonStates() {
    // Get the first navigation (the one the slider component controls)
    const firstNav = this.sliderComponent.querySelector('.timeline__desktop-navigation');
    if (!firstNav) return;

    const firstPrev = firstNav.querySelector('button[name="previous"]');
    const firstNext = firstNav.querySelector('button[name="next"]');

    // Sync to all other navigation instances
    const allNavs = this.sliderComponent.querySelectorAll('.timeline__desktop-navigation');
    allNavs.forEach((nav) => {
      if (nav === firstNav) return; // Skip the first one

      const prevButton = nav.querySelector('button[name="previous"]');
      const nextButton = nav.querySelector('button[name="next"]');

      if (firstPrev && prevButton) {
        if (firstPrev.hasAttribute('disabled')) {
          prevButton.setAttribute('disabled', 'disabled');
        } else {
          prevButton.removeAttribute('disabled');
        }
      }

      if (firstNext && nextButton) {
        if (firstNext.hasAttribute('disabled')) {
          nextButton.setAttribute('disabled', 'disabled');
        } else {
          nextButton.removeAttribute('disabled');
        }
      }
    });
  }

  goToSlide(index) {
    if (index < 0 || index >= this.sliderItems.length) return;
    
    const targetSlide = this.sliderItems[index];
    if (!targetSlide) return;

    // Calculate scroll position
    const slideWidth = targetSlide.offsetWidth;
    const scrollPosition = slideWidth * index;
    
    // Ensure we don't scroll past the maximum
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    const clampedPosition = Math.min(scrollPosition, maxScroll);

    // Use the slider component's method to navigate
    const sliderComponent = this.sliderComponent;
    if (sliderComponent && sliderComponent.setSlidePosition) {
      sliderComponent.setSlidePosition(clampedPosition);
    } else {
      // Fallback: direct scroll
      this.slider.scrollTo({
        left: clampedPosition,
        behavior: 'smooth'
      });
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new TimelineSection();
  });
} else {
  new TimelineSection();
}

